import { Col, Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import * as St from './StoreSearch.styles';

import { useLazyFetchZipcodeLocationQuery } from '~/api/easy-estimator-api/stores-api/storesService';
import { useStores } from '~/api/easy-estimator-api/stores-api/useStores';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { handleIntegerNumberInput, i18KeyMaker } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectZipcode, updateCityAndState } from '~/redux/slices/zipcode';
import PageSubtitle from '~atoms/PageSubtitle';
import PageTitle from '~atoms/PageTitle';
import { useDrawingShapeContext } from '~contexts';
import FullPageLoader from '~templates/FullPageLoader';

type StoreSearchProps = {
  showZipCodeInput?: boolean;
  disabled?: boolean;
  onSubmitWithNonEmptyDrawingArea: (newZipcode: string) => void;
  onSubmitWithEmptyDrawingArea: (newZipcode: string) => void;
};

const fieldNameForValidation = 'zipCode';

const StoreSearch = ({
  showZipCodeInput = true,
  disabled = false,
  onSubmitWithNonEmptyDrawingArea,
  onSubmitWithEmptyDrawingArea,
}: StoreSearchProps) => {
  const { isOnlineRetailer } = useRetailerSettings();
  const [fetchZipcodeLocation] = useLazyFetchZipcodeLocationQuery();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const initialZipcode = useAppSelector(selectZipcode);

  const [lastValidZipcode, setLastValidZipcode] = useState(initialZipcode);

  const { stores, isFetchingStores, zipCodeLocation } = useStores();

  const { retailerSettings } = useRetailerSettings();
  const {
    getters: { areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();

  const pageTitle = showZipCodeInput
    ? t('STORE_PAGE.TITLE')
    : t('STORE_PAGE.STORES');
  const pageSubTitle = retailerSettings?.isOnlineRetailer
    ? t('STORE_PAGE.SUBTITLE_ONLINE')
    : t('STORE_PAGE.SUBTITLE');

  const inputPlaceholder = t('STORE_PAGE.INPUT_PLACEHOLDER');

  const [_isLoadingZipCodeSearch, setIsLoadingZipCodeSearch] = useState(false);

  const isLoadingZipCodeSearch = isFetchingStores || _isLoadingZipCodeSearch;

  const submitZipCode = useCallback(
    async (value: string) => {
      try {
        flushSync(() => setIsLoadingZipCodeSearch(true));
        await form.validateFields();

        const { isError, data } = await fetchZipcodeLocation(value);

        // Checking for error, data and that data has any property to consider the zip code valid. No need to specifically check for city
        if (isError || !data || (!!data && !data.city)) {
          throw new Error('Invalid zipcode'); // Zipcodes that are not found are invalid
        }

        const inputValue = form.getFieldValue(fieldNameForValidation);

        if (
          inputValue &&
          areThereShapesInTheDrawingArea &&
          inputValue !== initialZipcode
        ) {
          onSubmitWithNonEmptyDrawingArea(value);
        } else {
          onSubmitWithEmptyDrawingArea(value);
          setLastValidZipcode(inputValue);
        }
      } catch (error) {
        form.setFields([
          {
            name: fieldNameForValidation,
            value: lastValidZipcode,
            errors: [`Invalid zip code${value ? ':' : ''} ${value}`],
          },
        ]);
      } finally {
        setIsLoadingZipCodeSearch(false);
      }
    },
    [
      areThereShapesInTheDrawingArea,
      fetchZipcodeLocation,
      form,
      initialZipcode,
      lastValidZipcode,
      onSubmitWithEmptyDrawingArea,
      onSubmitWithNonEmptyDrawingArea,
    ]
  );

  const showNoStoreMessage =
    !isFetchingStores && !!initialZipcode && !stores.length;

  // TODO This probably needs to be a retailer-specific message from settings so it can include a phone number, etc.
  const noStoreMessageKey = i18KeyMaker(
    'STORE_PAGE.',
    retailerSettings?.isOnlineRetailer ?? false,
    'NO_STORE',
    'NO_STORE_ONLINE'
  );

  const onZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    form.setFields([
      {
        name: fieldNameForValidation,
        errors: [],
      },
    ]);

    const value = (event.target as HTMLInputElement).value;

    if (value && value.length === 5 && value !== initialZipcode) {
      submitZipCode(value);
    }
  };

  useEffect(() => {
    if (zipCodeLocation?.city || zipCodeLocation?.state) {
      dispatch(
        updateCityAndState({
          city: zipCodeLocation.city,
          state: zipCodeLocation.state,
        })
      );
    }
  }, [dispatch, initialZipcode, zipCodeLocation?.city, zipCodeLocation?.state]);

  if (!retailerSettings) {
    return <FullPageLoader />;
  }

  return (
    <>
      <Col>
        {/** Hide 'stores' title when is online retailer */}
        {isOnlineRetailer || !showZipCodeInput ? null : (
          <PageTitle>{pageTitle}</PageTitle>
        )}
        {showZipCodeInput && (
          <>
            <PageSubtitle>{pageSubTitle}</PageSubtitle>
            <Form
              form={form}
              layout="vertical"
              initialValues={{ zipCode: initialZipcode }}
              onFinish={(form) => submitZipCode(form.zipCode)}
            >
              <St.SubmitContainer>
                <St.SubmitFormItem
                  validateTrigger={false}
                  name={fieldNameForValidation}
                  data-testid="zipcode-search"
                >
                  <St.ZipCodeInput
                    autoFocus={!disabled}
                    type="text"
                    placeholder={inputPlaceholder}
                    maxLength={5}
                    size="large"
                    variant="borderless"
                    allowClear={false}
                    onKeyDown={handleIntegerNumberInput}
                    onChange={onZipCodeChange}
                    loading={isLoadingZipCodeSearch}
                    onSearch={submitZipCode}
                    enterButton
                    disabled={disabled}
                  />
                </St.SubmitFormItem>
              </St.SubmitContainer>
            </Form>
          </>
        )}
      </Col>
      {showNoStoreMessage && <St.Text>{t(noStoreMessageKey)}</St.Text>}
    </>
  );
};

export default StoreSearch;
